import type { SuggestionKeyDownProps } from "@tiptap/suggestion";
import type { MaybeRefOrGetter } from "vue";
import { createEventHook, until, useToggle } from "@vueuse/core";
import { Keys } from "ls/common/lib/keyboard";
import { readonly, ref, toValue } from "vue";

export function useSuggestionListNavigation(listLength: MaybeRefOrGetter<number>) {
  const selectedIndex = ref(0);
  const selected = createEventHook<number>();
  const [isOpen, setOpen] = useToggle(true);

  function reset() {
    selectedIndex.value = 0;
  }

  function previous() {
    const previousIndex = selectedIndex.value - 1;
    if (previousIndex >= 0) {
      selectedIndex.value = previousIndex;
    }
  }

  function next() {
    const nextIndex = selectedIndex.value + 1;
    if (nextIndex <= toValue(listLength) - 1) {
      selectedIndex.value = nextIndex;
    }
  }

  function first() {
    selectedIndex.value = 0;
  }

  function last() {
    selectedIndex.value = toValue(listLength) - 1;
  }

  function select(index: number) {
    selectedIndex.value = index;
  }

  function selectCurrent() {
    void selected.trigger(selectedIndex.value);
  }

  function onKeyDown({ event }: SuggestionKeyDownProps) {
    event.stopPropagation();

    switch (event.key) {
      case Keys.ArrowUp:
        previous();
        return true;

      case Keys.ArrowDown:
        next();
        return true;

      case Keys.PageUp:
      case Keys.Home:
        first();
        return true;

      case Keys.PageDown:
      case Keys.End:
        last();
        return true;

      case Keys.Tab:
      case Keys.Enter:
        selectCurrent();
        return true;

      case Keys.Escape:
        setOpen(false);
        return true;

      default:
        return false;
    }
  }

  const suggestionLeft = ref(false);
  function onBeforeExit() {
    setOpen(false);
    // allow transition to complete before suggestion is fully unmounted
    // also provide a sensible timeout in case if transition didn't run at all
    // so that promise could still resolve
    return until(suggestionLeft).toBe(true, { timeout: 1000 });
  }

  function handleAfterLeave() {
    suggestionLeft.value = true;
  }

  return {
    isOpen: readonly(isOpen),
    setOpen,
    selectedIndex: readonly(selectedIndex),
    selectCurrent,
    select,
    previous,
    next,
    reset,
    selected,
    onKeyDown,
    onBeforeExit,
    handleAfterLeave,
  };
}
